<template>
  <div>
    <Button
      v-if="showButton"
      label="Add Task"
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="mdi-plus" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Create Task"
      :style="{ width: 'min(50rem, 95vw)' }"
      :modal="true"
    >
      <Tabs value="0">
        <TabList>
          <Tab value="0">
            <div class="flex items-center gap-1">
              <i class="pi pi-pen-to-square" />
              Task
            </div>
          </Tab>

          <Tab value="1">
            <div class="flex items-center gap-1">
              <i class="pi pi-paperclip" />
              Attachments
            </div>
          </Tab>

          <Tab
            value="2"
          >
            <div class="flex items-center gap-1">
              <i class="pi pi-share-alt" />
              Associations
            </div>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel value="0">
            <div class="grid grid-cols-4 gap-4 min-h-96">
              <div class="col-span-4">
                <div class="flex flex-col gap-2">
                  <label for="content">Description
                    <span class="text-red-500">*</span></label>

                  <Textarea
                    v-model="taskData.description"
                    rows="5"
                    cols="30"
                  />
                </div>
              </div>

              <div class="col-span-4">
                <div class="flex flex-col gap-2">
                  <label for="content">Due Date
                    <span class="text-red-500">*</span></label>

                  <DatePicker
                    id="datepicker-24h"
                    v-model="taskData.dueDate"
                    :dateFormat="$t('util.dateFormatWithMonth') + ' -'"
                    showTime
                    hourFormat="12"
                    stepMinute="15"
                    fluid
                    showButtonBar
                  />
                </div>
              </div>

              <div class="col-span-4">
                <div class="flex flex-col gap-2">
                  <label for="content">Assigned To
                    <span class="text-red-500">*</span></label>
                  <Select
                    v-model="taskData.assignedTo"
                    :options="users"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select an agent"
                  />
                </div>
              </div>
            </div>
          </TabPanel>

          <TabPanel value="1">
            <div class="h-80">
              <UploaderPublicStorage
                ref="uploaderRef"
                v-model:selectedFiles="stagedFiles"
                v-model:uploadedFiles="uploadedFiles"
                v-model:isSubmitting="isSubmitting"
                v-model:filesPath="filesPath"
              />
            </div>
          </TabPanel>

          <TabPanel value="2">
            <div class="flex flex-col gap-4 h-80">
              <div
                class="flex flex-col gap-2"
              >
                <label for="type">
                  Contact
                  <span class="text-xs">[optional]</span>
                </label>

                <ContactSelectSingle
                  v-model:selectedContact="selectedContact"
                  :showCreateContact="false"
                  :isDisabled="props.contactId ? true : false"
                />
              </div>

              <div
                class="flex flex-col gap-2"
              >
                <label for="type">
                  Business
                  <span class="text-xs">[optional]</span>
                </label>

                <Select
                  v-model="selectedBusiness"
                  class="w-full"
                  :options="businesses"
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select business"
                  filter
                  filterPlaceholder="Search businesses"
                  :disabled="props.businessId"
                />
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <template #footer>
        <div class="flex justify-end gap-2 px-5">
          <Button
            outlined
            label="Cancel"
            icon="pi pi-times"
            @click="hideDialog"
          />
          <Button
            label="Save"
            icon="pi pi-check"
            :loading="loading"
            @click="saveTask"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { Business } from '@prisma/client'

const props = defineProps({
  contactId: String,
  businessId: String,
})

const emit = defineEmits(['refresh'])

const { createTask } = useTasks()
const toast = useToast()
const route = useRoute()

// Dialog
const visible = ref(false)

const showButton = defineModel<boolean>('showButton', {
  required: false,
  default: true,
})

const toggleOpen = async () => {
  visible.value = true
  await init()
}

defineExpose({
  toggleOpen,
})

const hideDialog = () => {
  visible.value = false
  submitted.value = false
  resetForm()
}

// Form
const loading = ref(false)
const submitted = ref(false)

// Default DueDate Value
const today = ref(new Date())
today.value.setHours(9)
today.value.setMinutes(0)
today.value.setSeconds(0)
today.value.setMilliseconds(0)

const tomorrow = ref(new Date(today.value))
tomorrow.value.setDate(today.value.getDate() + 1)

const currentUserId = ref<string>('')

const taskData = ref({
  description: '',
  dueDate: tomorrow,
  assignedTo: '',
})

const errors = ref({
  description: false,
  dueDate: false,
})

const resetForm = () => {
  taskData.value = {
    description: '',
    dueDate: tomorrow,
    assignedTo: '',
  }

  errors.value = {
    description: false,
    dueDate: false,
  }
}

const validateForm = (): boolean => {
  errors.value.description = !taskData.value.description
  errors.value.dueDate = !taskData.value.dueDate

  return !Object.values(errors.value).some(Boolean)
}

// Files Uploader
const filesPath = ref('tasks')
const stagedFiles = ref([])
const uploaderRef = ref(null)
const isSubmitting = ref(false)
const uploadedFiles = ref<JSON[]>([])

const uploadAttachments = async () => {
  await uploaderRef.value?.uploadFiles()
}

// Users
const { getUsers } = useUsers()
const users = ref()

// Current User
const { getUserWorkspace } = useUserWorkSpace()

// Associations
const { fetchContactLinkedBusinesses } = useContacts()
const { getAllPageAccessByBusinessId } = usePageAccess()
const contacts = ref()
const selectedContact = ref()

const { getAllBusinesses } = useBusiness()
const businesses = ref()
const selectedBusiness = ref()

// Save Task
const saveTask = async () => {
  submitted.value = true

  const isValid = validateForm()

  if (errors.value.description && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Description is required',
      life: 3000,
    })
  }

  if (errors.value.dueDate && submitted) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Due date is required',
      life: 3000,
    })
  }

  if (!isValid) return

  loading.value = true

  try {
    const contactId = selectedContact.value
    const businessId = selectedBusiness.value

    // Upload attachments
    const taskAttachments = ref<JSON[]>([])
    console.log('Uploading task attachments')
    await uploadAttachments()

    if (uploadedFiles.value.length > 0) {
      taskAttachments.value = uploadedFiles.value
    }

    const { data, error } = await createTask(
      {
        description: taskData.value.description,
        dueDate: taskData.value.dueDate,
        assignedTo: taskData.value.assignedTo,
        attachments: taskAttachments.value,
      },
      contactId,
      businessId,
    )

    if (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create task',
        life: 3000,
      })
      return
    }

    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Task created successfully',
      life: 3000,
    })

    hideDialog()
    emit('refresh')
  }
  catch (error) {
    console.error('Error creating task:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create task',
      life: 3000,
    })
  }
  finally {
    loading.value = false
  }
}

const init = async () => {
  if (props.contactId) {
    selectedContact.value = props.contactId
  }

  if (props.businessId) {
    selectedBusiness.value = props.businessId
  }

  if (currentUserId.value) {
    taskData.value.assignedTo = currentUserId.value
  }
}

onMounted(async () => {
  await init()

  const resUsers = await getUsers()
  users.value = resUsers.data

  const resCurrentUser = await getUserWorkspace()
  if (resCurrentUser.data?.id) {
    currentUserId.value = resCurrentUser.data.id
  }

  if (route.path.startsWith('/contacts/') && props.contactId) {
    const resLinkedBusinesses = await fetchContactLinkedBusinesses(props.contactId)
    businesses.value = resLinkedBusinesses.map((business: {
      Business: Business
    }) => {
      return {
        id: business.Business.id,
        name: business.Business.name,
      }
    })
  }
  else {
    const resBusinesses = await getAllBusinesses()
    businesses.value = resBusinesses
  }

  if (route.path.startsWith('/businesses/') && props.businessId) {
    const resPageAccess = await getAllPageAccessByBusinessId(props.businessId)
    contacts.value = resPageAccess.data?.filter(item => item.contactId).map((record) => {
      return {
        id: record.contactId,
        name: record.firstName + ' ' + record.lastName,
      }
    })
  }
})
</script>
